import { TakweenKey } from '@peekapak/types/DataProtocolTypes';
import { isUserHasKey, getLicenseLevel } from '../GlobalFunctions';

export const hasPeekapakLibraryAccess = (): boolean | null => isUserHasKey(TakweenKey.PEEKAPAK_LIBRARY);

// KEY_TAKWEEN_LIBRARY pre-dates the split into ALL_UNITS/BUILDING_RESILIENCE/FUTURE_READY keys.
// Treated as equivalent to ALL_UNITS so any account still holding it doesn't lose access before
// the keyRing migration (PE-1528 Part 2) runs. Remove once that migration confirms it's unused.
export const hasBuildingResilienceAccess = (): boolean | null =>
  isUserHasKey(TakweenKey.TAKWEEN_ALL_UNITS) ||
  isUserHasKey(TakweenKey.TAKWEEN_LIBRARY) ||
  isUserHasKey(TakweenKey.TAKWEEN_BUILDING_RESILIENCE);

export const hasFutureReadyAccess = (): boolean | null =>
  isUserHasKey(TakweenKey.TAKWEEN_ALL_UNITS) ||
  isUserHasKey(TakweenKey.TAKWEEN_LIBRARY) ||
  isUserHasKey(TakweenKey.TAKWEEN_FUTURE_READY);

// For nav-level "show the Library tab at all" decisions - not grade-gated, since that only
// affects which units are visible within a library, not whether the library exists for this user.
// Coerced to a strict boolean (unlike the other checks above) because this one is consumed by
// nav header components outside this module - null (not-logged-in) must read as false, not as
// a truthy value that accidentally reveals the Library tab.
export const hasAnyLibraryAccess = (): boolean =>
  (hasPeekapakLibraryAccess() || hasBuildingResilienceAccess() || hasFutureReadyAccess()) === true;

// Building Resilience and Future Ready are never relevant below grade 6, regardless of license.
export const MINIMUM_TAKWEEN_GRADE = 6;

// Takween wins over Peekapak as the default active library when a user has both -
// matches the PRD matrix's "Default: BR" rows for WellbeingLead.
export const getDefaultActiveLibrary = (): 'peekapak' | 'takween' =>
  hasBuildingResilienceAccess() || hasFutureReadyAccess() ? 'takween' : 'peekapak';

// The switcher only shows when there's something to switch to and from - by design, that means
// having the Peekapak key specifically, not just any two Takween units. Coerced to a strict
// boolean since this is consumed directly by UI, same reasoning as hasAnyLibraryAccess.
export const shouldShowLibrarySwitcher = (): boolean =>
  (hasPeekapakLibraryAccess() && (hasBuildingResilienceAccess() || hasFutureReadyAccess())) === true;

export const isTakweenUnitAccessible = (
  requiredKey: TakweenKey | undefined,
  classroomGrade: number,
): boolean | null => {
  if (classroomGrade < MINIMUM_TAKWEEN_GRADE) return false;
  if (requiredKey === TakweenKey.TAKWEEN_BUILDING_RESILIENCE) return hasBuildingResilienceAccess();
  if (requiredKey === TakweenKey.TAKWEEN_FUTURE_READY) return hasFutureReadyAccess();
  // Fail closed: a unit with no requiredKey, or one that isn't a recognized Takween key, is never
  // unlockable via this check - there's no access rule to grant it against.
  return false;
};

// Real, un-migrated Takween account: licenseLevel TAKWEEN with none of the new split keys yet.
export const isLegacyTakweenAccount = (): boolean =>
  getLicenseLevel() === 'TAKWEEN' && !hasBuildingResilienceAccess() && !hasFutureReadyAccess();

// Gate every new-switcher call site on this - never true for a legacy account or an account with
// no Takween access at all.
export const isNewTakweenExperienceEnabled = (): boolean =>
  (hasBuildingResilienceAccess() || hasFutureReadyAccess()) && !isLegacyTakweenAccount();

// Source of truth for activeLibrary - falls back to the old licenseLevel check when off.
export const getEffectiveActiveLibrary = (): 'peekapak' | 'takween' =>
  isNewTakweenExperienceEnabled()
    ? getDefaultActiveLibrary()
    : getLicenseLevel() === 'TAKWEEN'
      ? 'takween'
      : 'peekapak';
